import { type PaginationOptions, usePagination } from 'vue-request';
import { computed } from 'vue';
import { useHttpInstance } from './use-http-instance.ts';
import {
  HTTP_DEFAULT_PAGE,
  HTTP_DEFAULT_PAGE_SIZE,
  HTTP_PAGE_KEY,
  HTTP_PAGE_SIZE_KEY,
  HTTP_RESPONSE_TOTAL_KEY,
  HTTP_RESPONSE_TOTAL_PAGES_KEY,
} from '../config.ts';
import type {
  HttpPaginatedParams,
  HttpPaginatedRequestFn,
  HttpPaginationData,
  UsePaginatedHttp,
  UsePaginatedHttpOptions,
} from './use-paginated-http.types.ts';
import type { HttpPaginatedResponse } from '../types.ts';

export function usePaginatedHttp<Response, Payload = undefined>(
  requestFn: HttpPaginatedRequestFn<Response, Payload>,
  {
    initialParams,
    ...options
  }: UsePaginatedHttpOptions<Response, Payload> = {} as UsePaginatedHttpOptions<
    Response,
    Payload
  >,
): UsePaginatedHttp<Response, Payload> {
  const unwrappedRequestFn = requestFn(useHttpInstance());
  const { current, pageSize, totalPage, total, ...result } = usePagination(
    unwrappedRequestFn,
    {
      ...(options as Omit<
        PaginationOptions<
          HttpPaginatedResponse<Response>,
          [HttpPaginatedParams<Payload>]
        >,
        'defaultParams'
      >),
      pagination: {
        currentKey: HTTP_PAGE_KEY,
        pageSizeKey: HTTP_PAGE_SIZE_KEY,
        totalKey: HTTP_RESPONSE_TOTAL_KEY,
        totalPageKey: HTTP_RESPONSE_TOTAL_PAGES_KEY,
      },
      defaultParams: [
        {
          page: HTTP_DEFAULT_PAGE,
          take: HTTP_DEFAULT_PAGE_SIZE,
          ...initialParams,
        } as HttpPaginatedParams<Payload>,
      ],
    },
  );
  const pagination = computed<HttpPaginationData>(() => ({
    currentPage: current.value,
    rowsPerPage: pageSize.value,
    count: total.value,
  }));
  return { pagination, ...result } as unknown as UsePaginatedHttp<
    Response,
    Payload
  >;
}
